import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X, ArrowRight, ArrowLeft, Loader2, Check, Sparkles,
  Instagram, Music2, Facebook, Share2, Copy, PartyPopper,
} from "lucide-react";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Static option sets ---------- */
const PLATFORMS = [
  "DoorDash","Uber","Lyft","Instacart","Spark","Roadie","Other",
];
const GIG_EXPERIENCE = [
  { v: "lt-6mo",  l: "Less than 6 months" },
  { v: "6-12mo",  l: "6–12 months" },
  { v: "1-3yr",   l: "1–3 years" },
  { v: "3yr+",    l: "3+ years" },
];
const FREQUENCIES = [
  { v: "full-time", l: "Full-time", d: "30+ hours/week" },
  { v: "part-time", l: "Part-time", d: "10–30 hours/week" },
  { v: "occasionally", l: "Occasionally", d: "Whenever I can" },
];
const EARNINGS = [
  { v: "under-250", l: "Under $250" },
  { v: "250-500",  l: "$250 – $500" },
  { v: "500-1000", l: "$500 – $1,000" },
  { v: "1000+",    l: "$1,000+" },
];
const ADVANCES = [
  { v: "50",   l: "$50" },
  { v: "100",  l: "$100" },
  { v: "200",  l: "$200" },
  { v: "500+", l: "$500+" },
];
const CHALLENGES = [
  "Gas","Car repairs","Bills before payday","Emergency expenses","Slow payout schedules","Other",
];
const URGENCIES = [
  { v: "same-day",     l: "Same day",          d: "Within hours of earning" },
  { v: "within-hours", l: "Within hours",      d: "A few hours feels great" },
  { v: "next-morning", l: "Next morning",      d: "Overnight is fine" },
  { v: "weekly",       l: "Weekly flexibility", d: "Smooth out the week" },
];
const ACQUISITION_SOURCES = ["TikTok","Instagram","Facebook","Friend","Google","Other"];
const GAP_FREQUENCIES = [
  { v: "weekly",       l: "Weekly" },
  { v: "few-monthly",  l: "A few times a month" },
  { v: "occasionally", l: "Occasionally" },
  { v: "rarely",       l: "Rarely" },
];
const USAGE_FREQUENCIES = [
  { v: "weekly",       l: "Weekly" },
  { v: "bi-weekly",    l: "Bi-weekly" },
  { v: "monthly",      l: "Monthly" },
  { v: "emergencies",  l: "Emergencies only" },
];
const MEMBERSHIP = [
  { v: "yes",   l: "Yes" },
  { v: "maybe", l: "Maybe" },
  { v: "no",    l: "No" },
];
const ACCOUNT_CONNECT = [
  { v: "yes",   l: "Yes" },
  { v: "maybe", l: "Maybe" },
  { v: "no",    l: "No" },
];
const USAGE_INTENT = [
  { v: "very-likely",     l: "Very likely" },
  { v: "somewhat-likely", l: "Somewhat likely" },
  { v: "neutral",         l: "Neutral" },
  { v: "unlikely",        l: "Unlikely" },
];

/* Capture attribution + device on wizard mount */
const captureAttribution = () => {
  try {
    const url = new URL(window.location.href);
    const p = url.searchParams;
    const w = window.innerWidth;
    const ua = navigator.userAgent || "";
    const browser =
      /Edg\//.test(ua) ? "Edge" :
      /Chrome\//.test(ua) ? "Chrome" :
      /Firefox\//.test(ua) ? "Firefox" :
      /Safari\//.test(ua) ? "Safari" : "Other";
    const osName =
      /Windows/.test(ua) ? "Windows" :
      /Mac OS X/.test(ua) ? "macOS" :
      /Android/.test(ua) ? "Android" :
      /iPhone|iPad|iOS/.test(ua) ? "iOS" :
      /Linux/.test(ua) ? "Linux" : "Other";
    return {
      utm_source: p.get("utm_source") || null,
      utm_medium: p.get("utm_medium") || null,
      utm_campaign: p.get("utm_campaign") || null,
      utm_content: p.get("utm_content") || null,
      utm_term: p.get("utm_term") || null,
      device_type: w < 768 ? "mobile" : w < 1024 ? "tablet" : "desktop",
      user_agent: ua.slice(0, 240),
      referrer_url: (document.referrer || "").slice(0, 240) || null,
      landing_page: (window.location.pathname || "/").slice(0, 240),
      browser,
      os: osName,
      screen_resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    };
  } catch { return {}; }
};

const makeSessionId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* noop */ }
  return `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/* ---------- UI primitives ---------- */
const Field = ({ label, children, hint }) => (
  <label className="block mb-4">
    <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">{label}</span>
    {children}
    {hint && <span className="block mt-1.5 text-[11px] text-white/35">{hint}</span>}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full h-12 px-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all"
  />
);

const OptionButton = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left px-4 py-3.5 rounded-2xl border transition-all flex items-center justify-between gap-3
      ${active
        ? "bg-[#00FF88]/10 border-[#00FF88]/50 text-white shadow-[0_0_0_3px_rgba(0,255,136,0.08)]"
        : "bg-[#0A0A0A] border-white/8 text-white/80 hover:border-white/20 hover:bg-white/[0.02]"}`}
  >
    <span className="flex-1">{children}</span>
    {active && (
      <span className="w-5 h-5 rounded-full bg-[#00FF88] grid place-items-center shrink-0">
        <Check className="w-3 h-3 text-black" />
      </span>
    )}
  </button>
);

const Pill = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-all
      ${active
        ? "bg-[#00FF88] text-black border-[#00FF88]"
        : "bg-[#0A0A0A] text-white/75 border-white/10 hover:border-white/30"}`}
  >
    {children}
  </button>
);

/* ---------- Steps ---------- */
const TOTAL_STEPS = 6;

const Step1 = ({ data, set }) => (
  <div data-testid="wizard-step-1">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">First, the basics</h2>
    <p className="text-white/55 mb-7">So we know who you are when access opens.</p>

    <Field label="Full name">
      <Input
        value={data.full_name || ""}
        onChange={(e) => set({ full_name: e.target.value })}
        placeholder="Alex Morgan"
        autoFocus
        data-testid="wizard-full-name"
      />
    </Field>
    <Field label="Email">
      <Input
        type="email"
        value={data.email || ""}
        onChange={(e) => set({ email: e.target.value })}
        placeholder="you@example.com"
        data-testid="wizard-email"
      />
    </Field>
    <Field label="Phone number" hint="For SMS launch alerts">
      <Input
        type="tel"
        value={data.phone || ""}
        onChange={(e) => set({ phone: e.target.value })}
        placeholder="(555) 555-0100"
        data-testid="wizard-phone"
      />
    </Field>

    <div className="grid grid-cols-2 gap-3">
      <Field label="City">
        <Input
          value={data.city || ""}
          onChange={(e) => set({ city: e.target.value })}
          placeholder="Atlanta"
          data-testid="wizard-city"
        />
      </Field>
      <Field label="State">
        <Input
          value={data.state || ""}
          onChange={(e) => set({ state: e.target.value.toUpperCase().slice(0, 2) })}
          placeholder="GA"
          maxLength={2}
          data-testid="wizard-state"
        />
      </Field>
    </div>
    <Field label="ZIP code">
      <Input
        type="text"
        inputMode="numeric"
        value={data.zip_code || ""}
        onChange={(e) => set({ zip_code: e.target.value.replace(/[^\d-]/g, "").slice(0, 10) })}
        placeholder="30301"
        data-testid="wizard-zip"
      />
    </Field>
  </div>
);

const Step2 = ({ data, set }) => {
  const togglePlatform = (p) => {
    const current = data.platforms || [];
    const next = current.includes(p) ? current.filter((x) => x !== p) : [...current, p];
    const patch = { platforms: next };
    // Auto-default primary to first selected if not yet set, or clear if removed
    if (!data.primary_platform && next.length > 0) patch.primary_platform = next[0];
    if (data.primary_platform && !next.includes(data.primary_platform)) patch.primary_platform = next[0] || "";
    set(patch);
  };
  const selected = data.platforms || [];
  return (
    <div data-testid="wizard-step-2">
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Where do you earn?</h2>
      <p className="text-white/55 mb-2">Pick every platform you earn on. Multi-app away.</p>
      <p className="text-[11px] font-mono-c text-[#00FF88]/80 mb-6">
        {selected.length === 0 ? "Select one or more" : `${selected.length} selected`}
      </p>

      <div className="flex flex-wrap gap-2 mb-7">
        {PLATFORMS.map((p) => (
          <Pill
            key={p}
            active={selected.includes(p)}
            onClick={() => togglePlatform(p)}
            testid={`wizard-platform-${p.toLowerCase().replace(/\s/g,"-")}`}
          >
            {p}
          </Pill>
        ))}
      </div>

      {selected.length > 0 && (
        <div className="mb-7">
          <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
            Which one is your primary platform?
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {selected.map((p) => (
              <OptionButton
                key={p}
                active={data.primary_platform === p}
                onClick={() => set({ primary_platform: p })}
                testid={`wizard-primary-${p.toLowerCase().replace(/\s/g,"-")}`}
              >
                {p}
              </OptionButton>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
          How long have you been a gig worker?
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {GIG_EXPERIENCE.map((g) => (
            <OptionButton
              key={g.v}
              active={data.gig_experience === g.v}
              onClick={() => set({ gig_experience: g.v })}
              testid={`wizard-experience-${g.v}`}
            >
              {g.l}
            </OptionButton>
          ))}
        </div>
      </div>
    </div>
  );
};

const Step3 = ({ data, set }) => (
  <div data-testid="wizard-step-3">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">How's your earning week?</h2>
    <p className="text-white/55 mb-7">Helps us understand your rhythm.</p>

    <div className="mb-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">How often do you work gig apps?</div>
      <div className="space-y-2.5">
        {FREQUENCIES.map((f) => (
          <OptionButton
            key={f.v}
            active={data.frequency === f.v}
            onClick={() => set({ frequency: f.v })}
            testid={`wizard-frequency-${f.v}`}
          >
            <span className="block font-semibold">{f.l}</span>
            <span className="block text-xs text-white/45 mt-0.5">{f.d}</span>
          </OptionButton>
        ))}
      </div>
    </div>

    <div className="mb-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Average weekly gig earnings</div>
      <div className="grid grid-cols-2 gap-2.5">
        {EARNINGS.map((e) => (
          <OptionButton
            key={e.v}
            active={data.earnings_range === e.v}
            onClick={() => set({ earnings_range: e.v })}
            testid={`wizard-earnings-${e.v}`}
          >
            {e.l}
          </OptionButton>
        ))}
      </div>
    </div>

    <div>
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        How often do you experience financial gaps between payouts?
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {GAP_FREQUENCIES.map((g) => (
          <OptionButton
            key={g.v}
            active={data.gap_frequency === g.v}
            onClick={() => set({ gap_frequency: g.v })}
            testid={`wizard-gap-${g.v}`}
          >
            {g.l}
          </OptionButton>
        ))}
      </div>
    </div>
  </div>
);

const Step4 = ({ data, set }) => (
  <div data-testid="wizard-step-4">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Picture this</h2>
    <p className="text-white/55 mb-7">A little context goes a long way.</p>

    <div className="mb-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        If approved for a future advance, what amount would help most?
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {ADVANCES.map((a) => (
          <OptionButton
            key={a.v}
            active={data.ideal_advance === a.v}
            onClick={() => set({ ideal_advance: a.v })}
            testid={`wizard-advance-${a.v}`}
          >
            {a.l}
          </OptionButton>
        ))}
      </div>
    </div>

    <div className="mb-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        What financial challenge affects you most between payouts?
      </div>
      <div className="space-y-2.5">
        {CHALLENGES.map((c) => (
          <OptionButton
            key={c}
            active={data.top_challenge === c}
            onClick={() => set({ top_challenge: c })}
            testid={`wizard-challenge-${c.toLowerCase().replace(/\s/g,"-")}`}
          >
            {c}
          </OptionButton>
        ))}
      </div>
    </div>

    <div>
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        If approved, how often would you likely use GigAfloat?
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {USAGE_FREQUENCIES.map((u) => (
          <OptionButton
            key={u.v}
            active={data.usage_frequency === u.v}
            onClick={() => set({ usage_frequency: u.v })}
            testid={`wizard-usage-${u.v}`}
          >
            {u.l}
          </OptionButton>
        ))}
      </div>
    </div>
  </div>
);

const Step5 = ({ data, set }) => (
  <div data-testid="wizard-step-5">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">When you need it most</h2>
    <p className="text-white/55 mb-7">Timing matters for gig workers.</p>

    <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
      How quickly would access to advances help you most?
    </div>
    <div className="space-y-2.5">
      {URGENCIES.map((u) => (
        <OptionButton
          key={u.v}
          active={data.urgency === u.v}
          onClick={() => set({ urgency: u.v })}
          testid={`wizard-urgency-${u.v}`}
        >
          <span className="block font-semibold">{u.l}</span>
          <span className="block text-xs text-white/45 mt-0.5">{u.d}</span>
        </OptionButton>
      ))}
    </div>

    <div className="mt-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        Would you pay a small monthly membership for priority access to advances and premium benefits?
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {MEMBERSHIP.map((m) => (
          <OptionButton
            key={m.v}
            active={data.membership_interest === m.v}
            onClick={() => set({ membership_interest: m.v })}
            testid={`wizard-membership-${m.v}`}
          >
            {m.l}
          </OptionButton>
        ))}
      </div>
      <p className="text-[11px] text-white/35 mt-2 italic">Just gauging interest. No commitment.</p>
    </div>

    <div className="mt-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        If GigAfloat could securely verify your earnings, would you connect your gig account?
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {ACCOUNT_CONNECT.map((a) => (
          <OptionButton
            key={a.v}
            active={data.account_connection_willingness === a.v}
            onClick={() => set({ account_connection_willingness: a.v })}
            testid={`wizard-account-${a.v}`}
          >
            {a.l}
          </OptionButton>
        ))}
      </div>
    </div>

    <div className="mt-7">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        If approved, how likely are you to use GigAfloat?
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {USAGE_INTENT.map((u) => (
          <OptionButton
            key={u.v}
            active={data.usage_intent === u.v}
            onClick={() => set({ usage_intent: u.v })}
            testid={`wizard-usage-intent-${u.v}`}
          >
            {u.l}
          </OptionButton>
        ))}
      </div>
    </div>
  </div>
);

const Step6 = ({ data, set }) => (
  <div data-testid="wizard-step-6">
    <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Almost there</h2>
    <p className="text-white/55 mb-7">A few final details.</p>

    <div className="mb-6">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">How did you hear about GigAfloat?</div>
      <div className="grid grid-cols-2 gap-2.5">
        {ACQUISITION_SOURCES.map((s) => (
          <OptionButton
            key={s}
            active={data.acquisition_source === s}
            onClick={() => set({ acquisition_source: s, source: s })}
            testid={`wizard-acquisition-${s.toLowerCase().replace(/\s/g,"-")}`}
          >
            {s}
          </OptionButton>
        ))}
      </div>
    </div>

    <div className="mb-6">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">Want early beta access?</div>
      <div className="grid grid-cols-3 gap-2.5">
        {[
          { v: "yes",   l: "Yes, sign me up" },
          { v: "maybe", l: "Maybe" },
          { v: "no",    l: "Not now" },
        ].map((b) => (
          <OptionButton
            key={b.v}
            active={data.beta_interest === b.v}
            onClick={() => set({ beta_interest: b.v })}
            testid={`wizard-beta-${b.v}`}
          >
            {b.l}
          </OptionButton>
        ))}
      </div>
    </div>

    <div className="mb-6">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-3">
        Would you be open to a brief phone/Zoom interview to help shape GigAfloat?
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        {[
          { v: "yes", l: "Yes" },
          { v: "no",  l: "No" },
        ].map((o) => (
          <OptionButton
            key={o.v}
            active={data.interview_interest === o.v}
            onClick={() => set({ interview_interest: o.v })}
            testid={`wizard-interview-${o.v}`}
          >
            {o.l}
          </OptionButton>
        ))}
      </div>
    </div>

    <div className="mb-6">
      <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
        What would make GigAfloat most helpful for you?
      </div>
      <textarea
        value={data.helpful_feedback || ""}
        onChange={(e) => set({ helpful_feedback: e.target.value.slice(0, 280) })}
        placeholder="One sentence is plenty…"
        rows={3}
        className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all resize-none text-sm"
        data-testid="wizard-helpful-feedback"
      />
      <div className="flex justify-end mt-1">
        <span className="text-[10px] font-mono-c text-white/35">{(data.helpful_feedback || "").length}/280</span>
      </div>
    </div>

    <div className="rounded-2xl bg-white/[0.03] border border-white/8 p-4 mb-3">
      <label className="flex items-start gap-3 cursor-pointer" data-testid="wizard-consent-label">
        <input
          type="checkbox"
          checked={!!data.consent}
          onChange={(e) => set({ consent: e.target.checked })}
          className="mt-1 w-4 h-4 accent-[#00FF88] cursor-pointer"
          data-testid="wizard-consent-checkbox"
        />
        <span className="text-xs text-white/65 leading-relaxed">
          By joining the waitlist, you agree to receive updates, marketing emails, and occasional text messages from GigAfloat regarding early access, product updates, and future services. Message and data rates may apply. You can unsubscribe at any time.
        </span>
      </label>
    </div>

    <p className="text-[11px] text-white/35 leading-relaxed italic px-1">
      GigAfloat is currently in development. Joining the waitlist does not guarantee approval, funding, or financial services.
    </p>
    <p className="text-[11px] text-white/35 leading-relaxed mt-3 px-1">
      By continuing you agree to our{" "}
      <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-[#00FF88]/80 hover:text-[#00FF88]">Privacy Policy</a>
      {" "}and{" "}
      <a href="/terms" target="_blank" rel="noreferrer" className="underline text-[#00FF88]/80 hover:text-[#00FF88]">Terms of Service</a>.
    </p>
  </div>
);

/* ---------- Success screen ---------- */
const Success = ({ entry, onClose }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://gigafloat.app/?ref=${entry?.id?.slice(0, 8) || "you"}`;
  const shareText = "I just joined the GigAfloat waitlist — earnings-based Floats for gig workers. Bridging the gap between completed work and platform payouts 🚀";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Invite link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy"); }
  };

  const shareTo = (network) => {
    const enc = encodeURIComponent;
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      sms:      `sms:?body=${enc(shareText + " " + shareUrl)}`,
      copy:     null,
    };
    if (urls[network]) window.open(urls[network], "_blank", "noopener,noreferrer");
  };

  return (
    <div className="text-center py-4" data-testid="wizard-success">
      <div className="relative w-24 h-24 mx-auto mb-6">
        <div className="absolute inset-0 rounded-full bg-[#00FF88]/30 blur-2xl animate-pulse" />
        <div className="relative w-full h-full rounded-full bg-[#0A0A0A] border border-[#00FF88]/40 grid place-items-center edge-glow">
          <PartyPopper className="w-10 h-10 text-[#00FF88]" />
        </div>
      </div>

      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-3">
        You're officially <span className="bg-gradient-to-br from-[#00FF88] to-[#14F195] bg-clip-text text-transparent">on the list.</span>
      </h2>
      <p className="text-white/65 max-w-md mx-auto leading-relaxed">
        We're building GigAfloat for workers like you. Early supporters will receive updates, early access opportunities, and future launch announcements.
      </p>

      {entry?.position && (
        <div className="mt-6 inline-flex items-center gap-2 bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-full px-4 py-2">
          <Sparkles className="w-4 h-4 text-[#00FF88]" />
          <span className="font-mono-c text-sm text-[#00FF88]">Position #{entry.position}</span>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-white/[0.03] border border-white/8 p-5">
        <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-2">Invite friends · jump the line</div>
        <div className="text-sm text-white/65 mb-4">Every friend bumps you up the waitlist.</div>
        <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-2 mb-3">
          <div className="font-mono-c text-xs text-white/70 truncate flex-1 text-left">{shareUrl}</div>
          <button
            onClick={copy}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#00FF88] hover:text-white transition-colors"
            data-testid="wizard-copy-link"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button onClick={() => shareTo("facebook")} className="h-10 rounded-xl bg-[#0A0A0A] border border-white/10 hover:border-white/30 grid place-items-center" data-testid="wizard-share-facebook">
            <Facebook className="w-4 h-4 text-white/70" />
          </button>
          <button onClick={() => shareTo("sms")} className="h-10 rounded-xl bg-[#0A0A0A] border border-white/10 hover:border-white/30 grid place-items-center" data-testid="wizard-share-sms">
            <span className="text-xs font-bold text-white/70">SMS</span>
          </button>
          <button onClick={copy} className="h-10 rounded-xl bg-[#0A0A0A] border border-white/10 hover:border-white/30 grid place-items-center" data-testid="wizard-share-copy">
            {copied ? <Check className="w-4 h-4 text-[#00FF88]" /> : <Share2 className="w-4 h-4 text-white/70" />}
          </button>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center gap-3">
        <span className="text-[10px] uppercase font-mono-c tracking-widest text-white/40">Follow us</span>
        <a href="https://instagram.com/gigafloatapp" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/10 grid place-items-center hover:border-[#00FF88]/40" data-testid="follow-instagram">
          <Instagram className="w-4 h-4 text-white/70" />
        </a>
        <a href="https://www.tiktok.com/@gigafloatapp" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/10 grid place-items-center hover:border-[#00FF88]/40" data-testid="follow-tiktok">
          <Music2 className="w-4 h-4 text-white/70" />
        </a>
        <a href="https://www.facebook.com/share/1Cb84tk8uF/?mibextid=wwXIfr" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-full border border-white/10 grid place-items-center hover:border-[#00FF88]/40" data-testid="follow-facebook">
          <Facebook className="w-4 h-4 text-white/70" />
        </a>
      </div>

      <button
        onClick={onClose}
        className="mt-8 h-12 px-8 bg-[#00FF88] text-black font-bold rounded-full btn-glow"
        data-testid="wizard-success-close"
      >
        Back to GigAfloat
      </button>
    </div>
  );
};

/* ---------- Wizard root ---------- */
const WaitlistWizard = ({ open, onClose, prefillEmail = "" }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [entry, setEntry] = useState(null);
  const [success, setSuccess] = useState(false);
  const scrollRef = useRef(null);
  const startedAtRef = useRef(null);
  const sessionIdRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep(1);
      setData({ email: prefillEmail });
      setEntry(null);
      setSuccess(false);
      startedAtRef.current = new Date().toISOString();
      sessionIdRef.current = makeSessionId();
    }
  }, [open, prefillEmail]);

  // Body scroll lock + ESC close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Scroll to top on step change
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [step]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  // Per-step validation
  const isStepValid = () => {
    switch (step) {
      case 1: return /^\S+@\S+\.\S+$/.test(data.email || "") && (data.full_name || "").trim().length >= 2 && (data.phone || "").replace(/\D/g, "").length >= 10 && (data.city || "").trim().length >= 1 && (data.state || "").trim().length >= 2 && /^\d{5}(-\d{4})?$/.test((data.zip_code || "").trim());
      case 2: return Array.isArray(data.platforms) && data.platforms.length >= 1 && !!data.primary_platform && !!data.gig_experience;
      case 3: return !!data.frequency && !!data.earnings_range && !!data.gap_frequency;
      case 4: return !!data.ideal_advance && !!data.top_challenge && !!data.usage_frequency;
      case 5: return !!data.urgency && !!data.membership_interest && !!data.account_connection_willingness && !!data.usage_intent;
      case 6: return !!data.acquisition_source && !!data.beta_interest && !!data.interview_interest && (data.helpful_feedback || "").trim().length >= 2 && !!data.consent;
      default: return false;
    }
  };

  const next = async () => {
    if (!isStepValid()) {
      toast.error("Please complete the highlighted fields");
      return;
    }
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      return;
    }
    // FINAL submit
    setLoading(true);
    try {
      const startedAt = startedAtRef.current || new Date().toISOString();
      const completedAt = new Date().toISOString();
      const durSec = (new Date(completedAt) - new Date(startedAt)) / 1000;
      const attribution = captureAttribution();
      const refCode = (() => {
        try {
          return (
            new URLSearchParams(window.location.search).get("ref") ||
            sessionStorage.getItem("gf_ref") ||
            null
          );
        } catch { return null; }
      })();
      const { data: created } = await axios.post(`${API}/waitlist`, {
        email: data.email,
        full_name: data.full_name,
        phone: data.phone || null,
        city: data.city || null,
        state: data.state || null,
        zip_code: data.zip_code || null,
        interview_interest: data.interview_interest,
        platforms: data.platforms,
        primary_platform: data.primary_platform,
        gig_experience: data.gig_experience,
        frequency: data.frequency,
        earnings_range: data.earnings_range,
        ideal_advance: data.ideal_advance,
        top_challenge: data.top_challenge,
        urgency: data.urgency,
        source: data.acquisition_source || data.source,
        acquisition_source: data.acquisition_source,
        consent: data.consent,
        gap_frequency: data.gap_frequency,
        usage_frequency: data.usage_frequency,
        usage_intent: data.usage_intent,
        membership_interest: data.membership_interest,
        beta_interest: data.beta_interest,
        account_connection_willingness: data.account_connection_willingness,
        helpful_feedback: data.helpful_feedback || null,
        session_id: sessionIdRef.current,
        waitlist_started_at: startedAt,
        waitlist_completed_at: completedAt,
        completion_duration_sec: durSec,
        referred_by: refCode,
        ...attribution,
      });
      setEntry(created);
      setSuccess(true);
      // Tell the homepage social-proof counters to refresh
      try { window.dispatchEvent(new CustomEvent("gf:waitlist-joined")); } catch { /* noop */ }
    } catch (err) {
      toast.error("Couldn't submit", { description: err?.response?.data?.detail || "Try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-6 bg-[#050505]/85 backdrop-blur-md"
      data-testid="waitlist-wizard"
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onClose(); }}
    >
      <div className="relative w-full sm:max-w-lg h-full sm:h-auto sm:max-h-[92vh] bg-[#0A0A0A] sm:rounded-3xl border-0 sm:border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,255,136,0.25)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 sm:px-8 pt-6 pb-4 flex items-center justify-between border-b border-white/5 shrink-0">
          <div className="flex items-center gap-2.5">
            <LogoMark size={28} />
            <span className="font-display font-extrabold tracking-tight">GigAfloat</span>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 grid place-items-center text-white/60 hover:text-white hover:border-white/30 transition-colors disabled:opacity-40"
            aria-label="Close"
            data-testid="wizard-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress dots */}
        {!success && (
          <div className="px-6 sm:px-8 py-4 flex items-center gap-2 shrink-0" data-testid="wizard-progress">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full flex-1 transition-all duration-500 ${
                  i + 1 < step ? "bg-[#00FF88]" : i + 1 === step ? "bg-[#00FF88]/70" : "bg-white/8"
                }`}
              />
            ))}
            <span className="ml-3 font-mono-c text-[10px] tracking-widest text-white/40 shrink-0">
              {step}/{TOTAL_STEPS}
            </span>
          </div>
        )}

        {/* Body — scrollable */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 sm:px-8 py-6">
          {success ? (
            <Success entry={entry} onClose={onClose} />
          ) : (
            <div key={step} className="count-in">
              {step === 1 && <Step1 data={data} set={set} />}
              {step === 2 && <Step2 data={data} set={set} />}
              {step === 3 && <Step3 data={data} set={set} />}
              {step === 4 && <Step4 data={data} set={set} />}
              {step === 5 && <Step5 data={data} set={set} />}
              {step === 6 && <Step6 data={data} set={set} />}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!success && (
          <div className="px-6 sm:px-8 py-5 border-t border-white/5 flex items-center justify-between gap-3 shrink-0 bg-[#0A0A0A]">
            <button
              onClick={back}
              disabled={step === 1 || loading}
              className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid="wizard-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={next}
              disabled={loading || !isStepValid()}
              className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wizard-next"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : step === TOTAL_STEPS ? (
                <>Join the waitlist <ArrowRight className="w-4 h-4" /></>
              ) : (
                <>Continue <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistWizard;
